import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomToggle from '../../../../../../components/CustomToggle';
import { COLLECT_STATUS_CONFIG } from '../../constants';
import {
  formatTenge,
  getBaseUnitPrice,
  getEffectiveUnitPrice,
  getProductImageUrl,
  getProductLineTotal,
  hasLineChange,
} from '../../helpers';
import {
  CollectProductData,
  CollectStatus,
  ProductExtraOptions,
  ProductMark,
} from '../../types';
import {
  DefectPanel,
  ExpiryPanel,
  LessQuantityPanel,
  OtherPricePanel,
  OutOfStockPanel,
} from './components/StatusPanels';
import * as Styled from './ProductCollectCard.styles';

interface ProductCollectCardProps {
  product: CollectProductData;
  mark?: ProductMark;
  extra: ProductExtraOptions;
  editable: boolean;
  onSetMark: (product: CollectProductData, mark: ProductMark) => void;
  onResetMark: (product: CollectProductData) => void;
  onExtraChange: (
    product: CollectProductData,
    changes: Partial<ProductExtraOptions>
  ) => void;
}

// Статусы, требующие ввода данных в inline-панели
const PANEL_STATUSES = [
  CollectStatus.OUT_OF_STOCK,
  CollectStatus.LESS,
  CollectStatus.EXPIRY,
  CollectStatus.DEFECT,
  CollectStatus.OTHER_PRICE,
];

const ProductCollectCard = ({
  product,
  mark,
  extra,
  editable,
  onSetMark,
  onResetMark,
  onExtraChange,
}: ProductCollectCardProps) => {
  const { t } = useTranslation();
  const [isImageBroken, setIsImageBroken] = useState<boolean>(false);
  const [activePanel, setActivePanel] = useState<CollectStatus | null>(null);

  const statusConfig = mark && COLLECT_STATUS_CONFIG[mark.status];
  const lineChanged = hasLineChange(mark);

  const handleStatusClick = (status: CollectStatus) => {
    if (PANEL_STATUSES.includes(status)) {
      setActivePanel(status);
      return;
    }
    onSetMark(product, { status });
  };

  const confirmMark = (newMark: ProductMark) => {
    onSetMark(product, newMark);
    setActivePanel(null);
  };

  const getMarkDetail = () => {
    if (!mark) return null;
    switch (mark.status) {
      case CollectStatus.OUT_OF_STOCK:
        return mark.outOfStockReason
          ? t(
              `OrderPage.Collect.OutOfStockModal.${
                mark.outOfStockReason === 'in_database'
                  ? 'IN_DATABASE'
                  : 'IN_FACT'
              }`
            )
          : null;
      case CollectStatus.LESS:
        return t('OrderPage.Collect.IN_SHELF', { quantity: mark.quantity });
      case CollectStatus.EXPIRY:
        return t('OrderPage.Collect.EXP_DATE', { date: mark.expDate });
      case CollectStatus.DEFECT:
        return mark.defectReason;
      case CollectStatus.OTHER_PRICE:
        return t('OrderPage.Collect.PRICE_DETAIL', {
          price: formatTenge(mark.newPrice || 0),
        });
      default:
        return null;
    }
  };

  const markDetail = getMarkDetail();

  return (
    <Styled.CardContainer
      accentBackground={statusConfig?.background}
      accentBorder={statusConfig?.border}
      isPending={!mark}
    >
      {editable && !mark && !activePanel && (
        <Styled.MarkStatusHeader>
          {t('OrderPage.Collect.MARK_STATUS')}
        </Styled.MarkStatusHeader>
      )}

      <Styled.CardInfo>
        <div className="image-container">
          {!isImageBroken && (
            <img
              src={getProductImageUrl(product.sku)}
              alt={product.name}
              onError={() => setIsImageBroken(true)}
            />
          )}
        </div>
        <div className="info-container">
          <div className="name-row">
            <span className="name">{product.name}</span>
            <span className="quantity-badge">× {product.quantity} шт</span>
          </div>
          {product.packing && <div className="packing">{product.packing}</div>}
          <div className="manufacturer">{product.manufacturer}</div>
          {Boolean(getBaseUnitPrice(product)) && (
            <div className="price">
              {lineChanged ? (
                <>
                  <span className="price-old">
                    {formatTenge(getBaseUnitPrice(product) * product.quantity)}
                  </span>
                  <span className="price-new">
                    {getEffectiveUnitPrice(product, mark) !==
                    getBaseUnitPrice(product)
                      ? `${formatTenge(
                          getEffectiveUnitPrice(product, mark)
                        )} × `
                      : `${formatTenge(getBaseUnitPrice(product))} × `}
                    {mark?.status === CollectStatus.LESS
                      ? mark.quantity
                      : product.quantity}{' '}
                    = {formatTenge(getProductLineTotal(product, mark))}
                  </span>
                </>
              ) : (
                `${formatTenge(getBaseUnitPrice(product))} × ${
                  product.quantity
                } = ${formatTenge(
                  getBaseUnitPrice(product) * product.quantity
                )}`
              )}
            </div>
          )}
        </div>
      </Styled.CardInfo>

      {/* Выбранный статус */}
      {editable && mark && statusConfig && !activePanel && (
        <Styled.SelectedStatusBar statusColor={statusConfig.color}>
          <div className="status-info">
            <span className="status-icon">{statusConfig.icon}</span>
            <span className="status-label">{t(statusConfig.fullLabelKey)}</span>
            {markDetail && <span className="status-detail">{markDetail}</span>}
          </div>
          <button
            type="button"
            className="change-button"
            onClick={() => onResetMark(product)}
          >
            {t('OrderPage.Collect.CHANGE_STATUS')}
          </button>
        </Styled.SelectedStatusBar>
      )}

      {/* Кнопки выбора статуса */}
      {editable && !mark && !activePanel && (
        <Styled.StatusButtonsRow>
          {Object.values(CollectStatus).map((status) => (
            <Styled.StatusButton
              key={status}
              type="button"
              buttonColor={COLLECT_STATUS_CONFIG[status].color}
              onClick={() => handleStatusClick(status)}
            >
              <span className="status-icon">
                {COLLECT_STATUS_CONFIG[status].icon}
              </span>
              <span className="status-text">
                {t(COLLECT_STATUS_CONFIG[status].labelKey)}
              </span>
            </Styled.StatusButton>
          ))}
        </Styled.StatusButtonsRow>
      )}

      {/* Inline-панели ввода данных */}
      {editable && activePanel === CollectStatus.OUT_OF_STOCK && (
        <OutOfStockPanel
          onConfirm={(outOfStockReason) =>
            confirmMark({
              status: CollectStatus.OUT_OF_STOCK,
              outOfStockReason,
            })
          }
          onCancel={() => setActivePanel(null)}
        />
      )}
      {editable && activePanel === CollectStatus.LESS && (
        <LessQuantityPanel
          product={product}
          onConfirm={(quantity) =>
            confirmMark({ status: CollectStatus.LESS, quantity })
          }
          onCancel={() => setActivePanel(null)}
        />
      )}
      {editable && activePanel === CollectStatus.EXPIRY && (
        <ExpiryPanel
          onConfirm={(expDate) =>
            confirmMark({ status: CollectStatus.EXPIRY, expDate })
          }
          onCancel={() => setActivePanel(null)}
        />
      )}
      {editable && activePanel === CollectStatus.DEFECT && (
        <DefectPanel
          onConfirm={(defectReason) =>
            confirmMark({ status: CollectStatus.DEFECT, defectReason })
          }
          onCancel={() => setActivePanel(null)}
        />
      )}
      {editable && activePanel === CollectStatus.OTHER_PRICE && (
        <OtherPricePanel
          product={product}
          onConfirm={(newPrice) =>
            confirmMark({ status: CollectStatus.OTHER_PRICE, newPrice })
          }
          onCancel={() => setActivePanel(null)}
        />
      )}

      {/* Нужен рецепт — видимое поле */}
      {editable && !activePanel && (
        <Styled.NeedReceiptRow isChecked={Boolean(extra.needReceipt)}>
          <span className="receipt-label">
            {t('OrderPage.Collect.ExtraOptions.NEED_RECEIPT')}
          </span>
          <CustomToggle
            id={`need-receipt-${product.sku}`}
            variant="green"
            checked={Boolean(extra.needReceipt)}
            onChange={(value) => onExtraChange(product, { needReceipt: value })}
          />
        </Styled.NeedReceiptRow>
      )}
    </Styled.CardContainer>
  );
};

export default ProductCollectCard;
